import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  applyTheme,
  getStoredTheme,
  getSystemTheme,
  isThemedRoute,
  storeTheme,
} from '../../utils/theme';

const ThemeContext = createContext({
  theme: 'light',
  isThemed: false,
  toggleTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
  const { pathname } = useLocation();
  const [theme, setTheme] = useState(() => getStoredTheme() || getSystemTheme());

  useEffect(() => {
    applyTheme(theme, pathname);
  }, [theme, pathname]);

  // Follow system changes until the visitor picks a theme themselves.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => {
      if (!getStoredTheme()) setTheme(event.matches ? 'dark' : 'light');
    };
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next = current === 'dark' ? 'light' : 'dark';
      storeTheme(next);
      return next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, isThemed: isThemedRoute(pathname), toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeProvider;
